// Réglages > Permissions — retour Julien 2026-06-01 (point #10).
// Vue lecture seule de RESTRICTED_FEATURES + 4 toggles éditables (Option B).
import { useMemo } from 'react';
import { Check, Lock, Shield, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  RESTRICTED_FEATURES,
  ROLE_LABELS,
  type CollaboratorRole,
  type Feature,
} from '../../services/auth/permissions';
import {
  EDITABLE_FEATURES,
  type EditableFeature,
} from '../../services/featureOverrides/featureOverridesService';
import {
  useFeatureOverrides,
  useUpdateFeatureOverride,
} from '../../hooks/queries/useFeatureOverrides';
import {
  useDossierPermissionScope,
  useUpdateDossierPermissionScope,
} from '../../hooks/queries/useCabinetSettings';
import type { DossierPermissionScope } from '../../services/cabinet/cabinetSettingsService';
import { selectIsAdmin, useAuthStore } from '../../store/useAuthStore';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Spinner } from '../ui/Spinner';

const ROLE_ORDER: CollaboratorRole[] = ['admin', 'lawyer', 'secretary', 'client_portal'];

const EDITABLE_SET = new Set<Feature>(EDITABLE_FEATURES as readonly Feature[]);

// Regroupement humain pour la lecture du tableau
const SECTIONS: { title: string; prefix: string[] }[] = [
  { title: 'Réglages cabinet',  prefix: ['settings_', 'design_system', 'user_impersonation'] },
  { title: 'Dossiers',          prefix: ['dossiers_'] },
  { title: 'Demandes (tickets)',prefix: ['tickets_'] },
  { title: 'Contacts',          prefix: ['contacts_'] },
  { title: 'Tâches',            prefix: ['tasks_'] },
  { title: 'Agenda & Calendrier', prefix: ['agenda_', 'calendar_', 'hearings_'] },
  { title: 'Messagerie',        prefix: ['messaging', 'dossier_channel_'] },
  { title: 'Fichiers & GED',    prefix: ['files_', 'cabinet_library'] },
  { title: 'Facturation & prestations', prefix: ['billing_', 'invoices_', 'time_entries_', 'time_tracking_', 'timesheet_', 'timer_', 'hourly_rate_'] },
  { title: 'IA & Intégrations', prefix: ['ai_', 'gmail_', 'ebarreau_'] },
  { title: 'Stats & journaux',  prefix: ['stats_', 'logs_'] },
  { title: 'Parapheur & Signature', prefix: ['signature_'] },
  { title: 'Portail client',    prefix: ['portal_', 'client_portal_'] },
  { title: 'Notifications & profil', prefix: ['notifications_', 'dashboard', 'profile_'] },
];

function sectionFor(feature: Feature): string {
  for (const s of SECTIONS) {
    if (s.prefix.some((p) => feature === p || feature.startsWith(p))) return s.title;
  }
  return 'Divers';
}

export function PermissionsTab() {
  const { data: overrides, isLoading } = useFeatureOverrides();
  const updateOverride = useUpdateFeatureOverride();
  const { data: dossierScope = 'global' } = useDossierPermissionScope();
  const updateDossierScope = useUpdateDossierPermissionScope();
  const isAdmin = useAuthStore(selectIsAdmin);

  const grouped = useMemo(() => {
    const map = new Map<string, Feature[]>();
    (Object.keys(RESTRICTED_FEATURES) as Feature[])
      .sort()
      .forEach((f) => {
        const sec = sectionFor(f);
        const arr = map.get(sec) ?? [];
        arr.push(f);
        map.set(sec, arr);
      });
    return Array.from(map.entries());
  }, []);

  const effectiveRoles = (feature: Feature): readonly CollaboratorRole[] => {
    if (EDITABLE_SET.has(feature) && overrides) {
      const ov = overrides.find((o) => o.feature === feature);
      if (ov) return ov.allowed_roles;
    }
    return RESTRICTED_FEATURES[feature] as readonly CollaboratorRole[];
  };

  return (
    <div className="w-full mr-auto space-y-8">
      <header>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-5 h-5 text-cime-primary" />
          <h1 className="text-xl font-semibold text-cime-text-primary">
            Permissions
          </h1>
        </div>
        <p className="text-sm text-cime-text-muted max-w-3xl">
          Vue d'ensemble des accès par rôle dans l'application. La plupart des règles sont
          figées (secret professionnel oblige). Quelques exceptions, listées ci-dessous,
          peuvent être ajustées par un administrateur.
        </p>
      </header>

      <section className="rounded-cime border border-neutral-200 bg-white p-4 space-y-3">
        <h2 className="text-base font-semibold text-cime-text-primary">
          Droits sur un dossier
        </h2>
        <p className="text-sm text-cime-text-muted max-w-3xl">
          En mode globale, un intervenant a accès aux documents, notes, facturation et
          temps du dossier dès qu’il est dans l’équipe — selon son rôle cabinet (matrice
          ci-dessous). Les curseurs par personne sur la carte équipe sont masqués.
          En mode par dossier, ces curseurs réapparaissent et les anciens réglages
          sont réappliqués.
        </p>
        <RadioGroup
          value={dossierScope}
          onValueChange={(v) => {
            if (!isAdmin) return;
            updateDossierScope.mutate(v as DossierPermissionScope);
          }}
          disabled={!isAdmin || updateDossierScope.isPending}
          className="space-y-2"
        >
          <label
            htmlFor="scope-global"
            className={cn(
              'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
              dossierScope === 'global' ? 'border-cime-primary bg-cime-primary/5' : 'border-neutral-200',
            )}
          >
            <RadioGroupItem value="global" id="scope-global" className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-cime-text-primary">Globale</span>
              <span className="block text-xs text-cime-text-muted">
                Appartenance au dossier + rôle cabinet. Défaut demandé par le cabinet.
              </span>
            </span>
          </label>
          <label
            htmlFor="scope-per-dossier"
            className={cn(
              'flex items-start gap-3 rounded-md border p-3 cursor-pointer',
              dossierScope === 'per_dossier' ? 'border-cime-primary bg-cime-primary/5' : 'border-neutral-200',
            )}
          >
            <RadioGroupItem value="per_dossier" id="scope-per-dossier" className="mt-0.5" />
            <span>
              <span className="block text-sm font-medium text-cime-text-primary">Par dossier</span>
              <span className="block text-xs text-cime-text-muted">
                Documents, notes, facturation et temps réglés sur chaque intervenant.
              </span>
            </span>
          </label>
        </RadioGroup>
        {updateDossierScope.isError && (
          <p className="text-sm text-red-600">
            {updateDossierScope.error instanceof Error
              ? updateDossierScope.error.message
              : 'Erreur'}
          </p>
        )}
      </section>

      {/* Toggles éditables */}
      <section>
        <h2 className="text-base font-semibold text-cime-text-primary mb-2">
          Règles ajustables par l'administrateur
        </h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8"><Spinner /></div>
        ) : (
          <div className="space-y-2">
            {EDITABLE_FEATURES.map((feature) => {
              const ov = overrides?.find((o) => o.feature === feature);
              const allowed = (ov?.allowed_roles ?? RESTRICTED_FEATURES[feature]) as CollaboratorRole[];
              return (
                <EditableRow
                  key={feature}
                  feature={feature}
                  allowedRoles={allowed}
                  saving={updateOverride.isPending && updateOverride.variables?.feature === feature}
                  onToggle={(role, next) => {
                    const newRoles = next
                      ? Array.from(new Set([...allowed, role]))
                      : allowed.filter((r) => r !== role);
                    updateOverride.mutate({ feature, allowedRoles: newRoles });
                  }}
                />
              );
            })}
          </div>
        )}
        {updateOverride.isError && (
          <p className="mt-2 text-sm text-red-600">
            {updateOverride.error instanceof Error ? updateOverride.error.message : 'Erreur'}
          </p>
        )}
      </section>

      {/* Tableau lecture seule */}
      <section>
        <h2 className="text-base font-semibold text-cime-text-primary mb-2 flex items-center gap-1.5">
          <Lock className="w-4 h-4 text-cime-text-muted" />
          Matrice complète (lecture seule)
        </h2>
        <p className="text-sm text-cime-text-muted mb-3">
          Pour modifier une règle hors de la liste ci-dessus, contactez le support technique.
        </p>

        <div className="space-y-6">
          {grouped.map(([section, features]) => (
            <div key={section} className="rounded-cime border border-neutral-200 overflow-hidden bg-white">
              <h3 className="text-sm font-semibold text-cime-text-primary px-4 py-2 border-b border-neutral-200 bg-neutral-50">
                {section}
              </h3>
              <table className="cime-table text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 w-1/2">Action</th>
                    {ROLE_ORDER.map((r) => (
                      <th key={r} className="text-center px-2 py-2">
                        {ROLE_LABELS[r]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {features.map((f) => {
                    const eff = effectiveRoles(f);
                    const isEditable = EDITABLE_SET.has(f);
                    return (
                      <tr key={f}>
                        <td className="px-4 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px] text-cime-text-primary">{f}</span>
                            {isEditable && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                Ajustable
                              </span>
                            )}
                          </div>
                        </td>
                        {ROLE_ORDER.map((r) => (
                          <td key={r} className="text-center px-2 py-1.5">
                            {eff.includes(r) ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600 inline" />
                            ) : (
                              <X className="w-3.5 h-3.5 text-neutral-300 inline" />
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ─── Ligne éditable ─────────────────────────────────────────

const FEATURE_LABELS: Record<EditableFeature, { label: string; description: string }> = {
  dossiers_create: {
    label: 'Créer un dossier',
    description: 'Qui peut créer de nouveaux dossiers depuis l\'app.',
  },
  tickets_assign: {
    label: 'Assigner une demande à un avocat',
    description: 'Qui peut attribuer les demandes entrantes aux avocats.',
  },
  contacts_import: {
    label: 'Importer des contacts',
    description: 'Qui peut importer des contacts en masse (CSV, etc.).',
  },
  billing_view: {
    label: 'Consulter la facturation',
    description: 'Qui voit les factures du cabinet (lecture seule). ⚠️ Exposition de données financières.',
  },
};

function EditableRow({
  feature, allowedRoles, saving, onToggle,
}: {
  feature: EditableFeature;
  allowedRoles: CollaboratorRole[];
  saving: boolean;
  onToggle: (role: CollaboratorRole, next: boolean) => void;
}) {
  const meta = FEATURE_LABELS[feature];
  const eligible: CollaboratorRole[] = ['admin', 'lawyer', 'secretary'];

  return (
    <div className="rounded-cime border border-neutral-200 bg-white px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-cime-text-primary">{meta.label}</p>
          <p className="text-xs text-cime-text-muted mt-0.5">{meta.description}</p>
          <p className="text-[10px] font-mono text-cime-text-muted mt-0.5">{feature}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {eligible.map((r) => {
            const active = allowedRoles.includes(r);
            const isAdminRole = r === 'admin';
            return (
              <button
                key={r}
                type="button"
                disabled={saving || isAdminRole}
                onClick={() => onToggle(r, !active)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-medium border transition',
                  active
                    ? 'bg-cime-primary text-white border-cime-primary'
                    : 'bg-white text-cime-text-muted border-neutral-200 hover:border-neutral-400',
                  (saving || isAdminRole) && 'opacity-60 cursor-not-allowed',
                )}
                title={isAdminRole ? 'Admin toujours autorisé' : undefined}
              >
                {ROLE_LABELS[r]}
                {isAdminRole && <Lock className="w-3 h-3 ml-1 inline" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
